import { Component, Injector, inject, ChangeDetectionStrategy } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NEVER, merge } from 'rxjs';

import { CommonModule } from '@angular/common';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { ControlValueAccessorConnectorComponent } from '../control-value-accessor.connector';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../directives/base-form-control.directive';
import {
  DropdownFormControlDirective,
  DropdownFormControlDirectiveInputs,
  DropdownFormControlDirectiveOutputs,
} from '../directives/dropdown-form-control.directive';
import {
  TextFormControlDirective,
  TextFormControlDirectiveInputs,
  TextFormControlDirectiveOutputs,
} from '../directives/text-form-control.directive';
import {
  TextInputFormControlDirective,
  TextInputFormControlDirectiveInputs,
  TextInputFormControlDirectiveOutputs,
} from '../directives/text-input-form-control.directive';

import { FormComponent } from '../form/form.component';
import { FormConnectorService } from '../forms.utils';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { uniqueId } from '@shared/utils';

export enum InputType {
  Text = 'text',
  Number = 'number',
  Password = 'password',
  Email = 'email',
  Date = 'date',
}

@Component({
  selector: 'ui-input',
  templateUrl: './input.component.html',
  imports: [CommonModule, ReactiveFormsModule, InputWrapperComponent, InputTextModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: InputComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: TextFormControlDirective,
      inputs: TextFormControlDirectiveInputs,
      outputs: TextFormControlDirectiveOutputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
      outputs: DropdownFormControlDirectiveOutputs,
    },
    {
      directive: TextInputFormControlDirective,
      inputs: TextInputFormControlDirectiveInputs,
      outputs: TextInputFormControlDirectiveOutputs,
    },
  ],
})
export class InputComponent extends ControlValueAccessorConnectorComponent {
  protected _form = inject(FormComponent, { optional: true, skipSelf: true });
  protected _formConnectorService = inject(FormConnectorService, { optional: true, skipSelf: true });
  inputType = InputType;
  submitted = false;

  randomInputId = uniqueId();

  base = inject(BaseFormControlDirective);
  text = inject(TextFormControlDirective);
  dropdown = inject(DropdownFormControlDirective);
  textInput = inject(TextInputFormControlDirective);

  constructor() {
    const injector = inject(Injector);

    super(injector);
    merge<boolean[]>(this._formConnectorService?.submitted$ ?? NEVER, this._form?.getSubmitted() ?? NEVER)
      .pipe(takeUntilDestroyed())
      .subscribe((_) => (this.submitted = _));
  }

  get paddingRight(): string {
    let pr = 0;

    if (this.text.appendText) pr += 25 + (this.text.appendText.length > 8 ? 3 : this.text.appendText.length) * 8;
    if (this.text.appendIcon) pr += 40;
    if (
      this.dropdown.clearable &&
      this.textInput._nativeInputElement &&
      this.textInput._nativeInputElement.nativeElement.value
    ) {
      if (this.text.appendText || this.text.appendIcon) pr += 25;
      else pr += 45;
    }
    if (!this.text.appendText && !this.text.appendIcon && !this.dropdown.clearable) pr = 8;
    return `${pr}px !important`;
  }
}
